import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import { assert } from 'common/utils';
import { differenceInMilliseconds } from 'date-fns';
import type { RootState } from 'store';

import { openNatterApi } from '../api/openNatterApi';
import { selectOpenNatterId } from './openNatter.selectors';

/**
 * Open Natter Actions
 * Thunks for API calls and actions for WebSocket management
 */

// ============================================================================
// Leave Reason Type
// ============================================================================

export type LeaveReason = 'USER_INITIATED' | 'TIMEOUT' | 'ERROR';

// ============================================================================
// Synchronous Actions (WebSocket Management)
// ============================================================================

/**
 * Connect to the WebSocket server (without subscribing to channels)
 * This is handled by the listener middleware
 */
export const connectSocket = createAction('openNatter/connectSocket');

/**
 * WebSocket connected successfully
 */
export const socketConnected = createAction('openNatter/socketConnected');

/**
 * WebSocket disconnected
 */
export const socketDisconnected = createAction('openNatter/socketDisconnected');

/**
 * Subscribe to Open Natter WebSocket channels
 * This is handled by the listener middleware
 */
export const subscribeToOpenNatter = createAction<{ id: string }>('openNatter/subscribe');

/**
 * Unsubscribe from Open Natter WebSocket channels
 * This is handled by the listener middleware
 */
export const unsubscribeFromOpenNatter = createAction<{ id: string }>('openNatter/unsubscribe');

/**
 * Countdown ended (for UI timer)
 */
export const countdownEnded = createAction('openNatter/countdownEnded');

// ============================================================================
// Async Thunks (API Calls)
// ============================================================================

/**
 * Mark user as ready to be matched with a partner.
 *
 * Triggers the POST /ready API call. On success, the server will send a
 * SelfJoinedWaitingRoom WebSocket message (with ready=true) that transitions the phase to ReadyToMatch.
 *
 * @param id - The Open Natter event ID
 * @returns Object containing the event ID on success
 * @throws Rejects with error if API call fails
 */
export const markReady = createAsyncThunk('openNatter/markReady', async (id: string, { dispatch, rejectWithValue }) => {
  try {
    logger.info('[OpenNatter Actions] Marking ready', { id });
    await dispatch(openNatterApi.endpoints.postReady.initiate(id)).unwrap();
    return { id };
  } catch (error) {
    logger.error('[OpenNatter Actions] Failed to mark ready', { error, id });
    return rejectWithValue(error);
  }
});

/**
 * Leave an Open Natter event and unsubscribe from WebSocket channels.
 *
 * Triggers the POST /leave API call with an optional reason. On success,
 * the server will send a SelfLeftEvent WebSocket message that transitions
 * the phase to Ended.
 *
 * @param id - The Open Natter event ID
 * @param reason - Optional reason for leaving (e.g., 'user_initiated', 'timeout')
 * @returns Object containing the event ID on success
 * @throws Rejects with error if API call fails
 */
export const leaveOpenNatter = createAsyncThunk(
  'openNatter/leaveOpenNatter',
  async ({ id, reason }: { id: string; reason?: LeaveReason }, { dispatch, rejectWithValue }) => {
    try {
      logger.info('[OpenNatter Actions] Leaving Open Natter', { id, reason });
      await dispatch(openNatterApi.endpoints.postLeave.initiate({ id, reason })).unwrap();
      return { id };
    } catch (error) {
      logger.error('[OpenNatter Actions] Failed to leave Open Natter', { error, id });
      return rejectWithValue(error);
    }
  }
);

// ============================================================================
// UI Actions (Used by Container Components)
// ============================================================================

/**
 * Cleanup state when leaving natter route
 */
const clear = createAction('[OpenNatter] Cleanup state');

/**
 * Join natter button clicked
 * Subscribes to WebSocket - backend will automatically send SelfJoinedWaitingRoom message
 */
const joinNatterClicked = createAsyncThunk('[OpenNatter] Join Clicked', async (_, { dispatch, getState }) => {
  const id = selectOpenNatterId(getState() as RootState);
  assert(id);
  dispatch(subscribeToOpenNatter({ id }));
});

/**
 * Ready for natter button clicked
 * Triggers the markReady API call
 */
const readyNatterClicked = createAsyncThunk('[OpenNatter] Ready Clicked', async (_, { dispatch, getState }) => {
  const id = selectOpenNatterId(getState() as RootState);
  assert(id);
  await dispatch(markReady(id)).unwrap();
});

/**
 * Leave natter button clicked
 * Triggers the leaveOpenNatter API call with USER_INITIATED reason
 */
const leaveNatterClicked = createAsyncThunk('[OpenNatter] Leave Clicked', async (_, { dispatch, getState }) => {
  const id = selectOpenNatterId(getState() as RootState);
  assert(id);
  await dispatch(leaveOpenNatter({ id, reason: 'USER_INITIATED' })).unwrap();
});

/**
 * User matched - waits for startsAt time before fulfilling
 * Used by listener to delay phase transition
 * Uses serverNow to calculate delay independent of client clock skew
 */
const userMatched = createAsyncThunk<void, { startsAt: string; serverNow: string }>(
  '[OpenNatter] User Matched',
  async ({ startsAt, serverNow }, { fulfillWithValue }) => {
    // Calculate delay using server times (independent of client clock)
    const delayMs = differenceInMilliseconds(new Date(startsAt), new Date(serverNow));
    if (delayMs <= 0) {
      return fulfillWithValue(undefined);
    }

    await new Promise((resolve) => {
      setTimeout(() => {
        resolve(undefined);
      }, delayMs);
    });
  }
);

/**
 * User unmatched - waits for startsAt time before fulfilling
 * Used by listener to delay phase transition
 * Uses serverNow to calculate delay independent of client clock skew
 */
const userUnmatched = createAsyncThunk<void, { startsAt: string; serverNow: string }>(
  '[OpenNatter] User Unmatched',
  async ({ startsAt, serverNow }, { fulfillWithValue }) => {
    // Calculate delay using server times (independent of client clock)
    const delayMs = differenceInMilliseconds(new Date(startsAt), new Date(serverNow));
    if (delayMs <= 0) {
      return fulfillWithValue(undefined);
    }

    await new Promise((resolve) => {
      setTimeout(() => {
        resolve(undefined);
      }, delayMs);
    });
  }
);

/**
 * Natter countdown ended
 */
const natterCountdownEnded = createAction('[OpenNatter] Countdown Ended');

/**
 * Post answers for end-of-natter questions
 */
const postAnswers = createAsyncThunk<void, string[]>(
  '[OpenNatter] Post Answers',
  async (answers, { dispatch, getState }) => {
    const id = selectOpenNatterId(getState() as RootState);
    assert(id);
    await dispatch(openNatterApi.endpoints.postAnswers.initiate({ id, answers })).unwrap();
  }
);

/**
 * Post rating for the natter experience
 */
const postRating = createAsyncThunk<void, number>(
  '[OpenNatter] Post Rating',
  async (rating, { dispatch, getState }) => {
    const id = selectOpenNatterId(getState() as RootState);
    assert(id);
    await dispatch(openNatterApi.endpoints.postRating.initiate({ id, rating })).unwrap();
  }
);

/**
 * Grouped actions for UI components
 * @deprecated Use individual action exports instead for better tree-shaking
 */
export const openNatterActions = {
  clear,
  joinNatterClicked,
  readyNatterClicked,
  leaveNatterClicked,
  userMatched,
  userUnmatched,
  natterCountdownEnded,
  postAnswers,
  postRating,
};

// Re-export as natterActions for backwards compatibility during migration
export { openNatterActions as natterActions };
